import { RUNTIME } from "../runtime";
function isNamedValue(value) {
    return typeof value === "object" && value != null && "name" in value;
}
function isPathedValue(value) {
    return typeof value === "object" && value != null && "path" in value;
}
function getLastPathSegment(pathStr) {
    const lastForwardSlash = pathStr.lastIndexOf("/");
    const lastBackSlash = pathStr.lastIndexOf("\\");
    const lastSlashIndex = Math.max(lastForwardSlash, lastBackSlash);
    return lastSlashIndex >= 0 ? pathStr.substring(lastSlashIndex + 1) : pathStr;
}
export async function newFormData() {
    let formdata;
    if (RUNTIME.type === "node" && RUNTIME.parsedVersion != null && RUNTIME.parsedVersion >= 18) {
        formdata = new Node18FormData();
    }
    else if (RUNTIME.type === "node") {
        formdata = new Node16FormData();
    }
    else {
        formdata = new WebFormData();
    }
    await formdata.setup();
    return formdata;
}
/**
 * Form Data Implementation for Node.js 18+
 */
export class Node18FormData {
    async setup() {
        this.fd = new (await import("formdata-node")).FormData();
    }
    append(key, value) {
        this.fd?.append(key, value);
    }
    getFileName(value, filename) {
        if (filename != null) {
            return filename;
        }
        if (isNamedValue(value)) {
            return value.name;
        }
        if (isPathedValue(value) && value.path) {
            return getLastPathSegment(value.path.toString());
        }
        return undefined;
    }
    async appendFile(key, value, fileName) {
        fileName = this.getFileName(value, fileName);
        if (value instanceof Blob) {
            this.fd?.append(key, value, fileName);
        }
        else {
            this.fd?.append(key, {
                type: undefined,
                name: fileName,
                [Symbol.toStringTag]: "File",
                stream() {
                    return value;
                },
            });
        }
    }
    async getRequest() {
        const encoder = new (await import("form-data-encoder")).FormDataEncoder(this.fd);
        return {
            body: (await import("readable-stream")).Readable.from(encoder),
            headers: encoder.headers,
            duplex: "half",
        };
    }
}
/**
 * Form Data Implementation for Node.js 16-18
 */
export class Node16FormData {
    async setup() {
        this.fd = new (await import("form-data")).default();
    }
    append(key, value) {
        this.fd?.append(key, value);
    }
    getFileName(value, filename) {
        if (filename != null) {
            return filename;
        }
        if (isNamedValue(value)) {
            return value.name;
        }
        if (isPathedValue(value) && value.path) {
            return getLastPathSegment(value.path.toString());
        }
        return undefined;
    }
    async appendFile(key, value, fileName) {
        fileName = this.getFileName(value, fileName);
        let bufferedValue;
        if (value instanceof Blob) {
            bufferedValue = Buffer.from(await value.arrayBuffer());
        }
        else {
            bufferedValue = value;
        }
        if (fileName == null) {
            this.fd?.append(key, bufferedValue);
        }
        else {
            this.fd?.append(key, bufferedValue, { filename: fileName });
        }
    }
    getRequest() {
        return {
            body: this.fd,
            headers: this.fd ? this.fd.getHeaders() : {},
        };
    }
}
/**
 * Form Data Implementation for Web
 */
export class WebFormData {
    async setup() {
        this.fd = new FormData();
    }
    append(key, value) {
        this.fd?.append(key, value);
    }
    getFileName(value, filename) {
        if (filename != null) {
            return filename;
        }
        if (isNamedValue(value)) {
            return value.name;
        }
        if (isPathedValue(value) && value.path) {
            return getLastPathSegment(value.path.toString());
        }
        return undefined;
    }
    async appendFile(key, value, fileName) {
        fileName = this.getFileName(value, fileName);
        if (value instanceof Blob) {
            this.fd?.append(key, value, fileName);
            return;
        }
        this.fd?.append(key, new Blob([value]), fileName);
    }
    getRequest() {
        return {
            body: this.fd,
            headers: {},
        };
    }
}
